import { ControlIcons } from 'owa-control-icons';
import { isFeatureEnabled } from 'owa-feature-flags';
import { FilterableMenuItem, FilterableMenuItemArgs } from 'owa-filterable-menu';
import loc from 'owa-localize';
import { EditButtonLabel } from 'owa-locstrings/lib/strings/editbuttonlabel.locstring.json';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { EditMode, getOrCreateSxSStoreData, lazySaveToCloudFromSxS } from 'owa-sxs-store';
import { PreviewPaneMode, lazySxSDataModule } from 'owa-sxsdata';
import { EditOptionsCommand } from 'owa-outlook-service-options';
import { EditAndReplyButtonLabel } from './getEditButtons.locstring.json';
import { EditInBrowserButtonLabel, EditInDesktopButtonLabel } from 'owa-attachment-full-view';
import { isCloudyAndUsingJsApi, editAndReply } from './getEditButtons';
import * as React from 'react';
import { FontIcon, ImageIcon } from '@fluentui/react/lib/Icon';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import { SaveToCloudStatus } from 'owa-attachment-savetocloud';
import AttachmentId from 'owa-service/lib/contract/AttachmentId';
import showWovenEditInDesktopPremiumUpsellPopup from 'owa-attachment-full-view/lib/components/WovenEditInDesktopPremiumUpsellPopup';
import {
    getOfficeAppIconProps,
    getOfficeAppDisplayName,
    getOfficeAppFromAttachment,
    editIn,
} from 'owa-attachment-full-view/lib/utils/ContextMenu/wovenContextMenuItemHelpers';
import getProjection from 'owa-popout-v2/lib/utils/getProjection';
import { findTabIdBySxSId } from 'owa-tab-store';
import { logUsage } from 'owa-analytics';
import { isPremiumConsumer } from 'owa-session-store';

const styles = require('./getEditDropDownButton.scss');
const classNames = require('classnames/bind').bind(styles);

export default function getEditDropDownButton(sxsId: string): FilterableMenuItem {
    const currentWindow: Window = getProjection(findTabIdBySxSId(sxsId))?.window || window;
    const sxsStore = getOrCreateSxSStoreData(sxsId);
    const officeOnlineApp = getOfficeAppFromAttachmentId(sxsStore.extendedViewState.attachmentId);
    const officeAppIcon = getOfficeAppIconProps(officeOnlineApp);
    const appDisplayName = getOfficeAppDisplayName(officeOnlineApp);
    const shouldShowDiamond = isConsumer() && !isPremiumConsumer();

    const defaultEditOption = lazySxSDataModule.tryImportForRender()?.getDefaultEditCommandOption();

    const renderDesktopMenuItem = () => {
        return (
            <>
                <ImageIcon
                    className={classNames('ms-ContextualMenu-icon', styles.menuIcon)}
                    imageProps={officeAppIcon}
                />
                <span className={classNames('ms-ContextualMenu-itemText', styles.menuText)}>
                    {loc(EditInDesktopButtonLabel, loc(appDisplayName))}
                </span>
                {shouldShowDiamond && (
                    <FontIcon
                        className={classNames(
                            'ms-ContextualMenu-icon',
                            styles.menuIcon,
                            styles.diamondIcon
                        )}
                        iconName={ControlIcons.Diamond}
                    />
                )}
            </>
        );
    };

    const menuItems: IContextualMenuItem[] = [
        {
            key: 'editInBrowser',
            text: loc(EditInBrowserButtonLabel),
            title: loc(EditInBrowserButtonLabel),
            iconProps: { imageProps: officeAppIcon },
            onClick: () => {
                const attachmentId = sxsStore.extendedViewState.attachmentId;
                const attachment = attachmentId && getAttachment(attachmentId);
                editIn(
                    true /* browser */,
                    currentWindow /* currentWindow */,
                    attachment /* attachmentModel */,
                    officeOnlineApp /* officeOnlineApp */,
                    isConsumer() /* isConsumer */,
                    sxsStore.extendedViewState.isCloudy /* isCloudy */,
                    'woven-editDropDown' /* parentComponentName */,
                    sxsStore.extendedViewState.saveToCloudStatus ===
                        SaveToCloudStatus.disabled /* isSaveToCloudDisabled */,
                    sxsStore.extendedViewState.saveToCloudStatus ===
                        SaveToCloudStatus.available /* isSaveToCloudSupported */,
                    async () => {
                        /* saveToCloud */
                        const saveToCloudFromSxS = await lazySaveToCloudFromSxS.import();
                        await saveToCloudFromSxS('SxSCommandBar', sxsId);
                    }
                );
                logUsage('WovenEditInBrowser', { entryPoint: 'SxSCommandBar' }, { isCore: true });

                if (isFeatureEnabled('wvn-stickyEditOptionsPreview')) {
                    updateDefaultEditOptionIfNeeded(
                        defaultEditOption,
                        EditOptionsCommand.EditInBrowser
                    );
                }
            },
        },
        {
            key: 'editInDesktop',
            text: loc(EditInDesktopButtonLabel, loc(appDisplayName)),
            title: loc(EditInDesktopButtonLabel, loc(appDisplayName)),
            iconProps: { iconName: ControlIcons.Edit },
            onClick: () => {
                if (isConsumer() && !isPremiumConsumer()) {
                    showWovenEditInDesktopPremiumUpsellPopup(officeOnlineApp, currentWindow);
                    logUsage(
                        'WovenEditInDesktopUpsellPopup',
                        { entryPoint: 'SxSCommandBar' },
                        { isCore: true }
                    );
                } else {
                    const attachmentId = sxsStore.extendedViewState.attachmentId;
                    const attachment = attachmentId && getAttachment(attachmentId);

                    editIn(
                        false /* browser */,
                        currentWindow /* currentWindow */,
                        attachment /* attachmentModel */,
                        officeOnlineApp /* officeOnlineApp */,
                        isConsumer() /* isConsumer */,
                        sxsStore.extendedViewState.isCloudy /* isCloudy */,
                        'woven-editDropDown' /* parentComponentName */,
                        sxsStore.extendedViewState.saveToCloudStatus ===
                            SaveToCloudStatus.disabled /* isSaveToCloudDisabled */,
                        sxsStore.extendedViewState.saveToCloudStatus ===
                            SaveToCloudStatus.available /* isSaveToCloudSupported */,
                        async () => {
                            /* saveToCloud */
                            const saveToCloudFromSxS = await lazySaveToCloudFromSxS.import();
                            await saveToCloudFromSxS('SxSCommandBar', sxsId);
                        }
                    );

                    logUsage(
                        'WovenEditInDesktopPremium',
                        { entryPoint: 'SxSCommandBar' },
                        { isCore: true }
                    );

                    if (isFeatureEnabled('wvn-stickyEditOptionsPreview')) {
                        updateDefaultEditOptionIfNeeded(
                            defaultEditOption,
                            EditOptionsCommand.EditInDesktop
                        );
                    }
                }
            },
            onRenderContent: renderDesktopMenuItem,
            onRenderIcon: renderDesktopMenuItem,
        },
        {
            key: 'editAndReply',
            text: loc(EditAndReplyButtonLabel),
            title: loc(EditAndReplyButtonLabel),
            iconProps: { iconName: ControlIcons.PencilReply },
            onClick: () => {
                editAndReply(sxsId);

                if (isFeatureEnabled('wvn-stickyEditOptionsPreview')) {
                    updateDefaultEditOptionIfNeeded(
                        defaultEditOption,
                        EditOptionsCommand.EditAndReply
                    );
                }
            },
        },
    ];

    if (isFeatureEnabled('wvn-stickyEditOptionsPreview')) {
        const defaultButton = menuItems.splice(defaultEditOption, 1)[0];

        const editSplitMenu = new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: defaultButton.title,
            [FilterableMenuItemArgs.shouldShow]: () => {
                const isGroup =
                    sxsStore.editState.itemId?.mailboxInfo &&
                    sxsStore.editState.itemId.mailboxInfo.type === 'GroupMailbox';
                return (
                    isFeatureEnabled('wvn-sxsEditButtonSplit') &&
                    officeOnlineApp !== null &&
                    sxsStore.readingPaneState &&
                    sxsStore.viewState?.previewPane &&
                    sxsStore.viewState.previewPane.mode === PreviewPaneMode.Wac &&
                    sxsStore.editState.editAllowed &&
                    sxsStore.editState.mode === EditMode.View &&
                    sxsStore.editState.itemId &&
                    !sxsStore.editState.isDraft &&
                    (!isGroup || !isConsumer()) &&
                    !isCloudyAndUsingJsApi(sxsStore)
                );
            },
            [FilterableMenuItemArgs.ariaLabel]: defaultButton.text,
            [FilterableMenuItemArgs.split]: true,
            [FilterableMenuItemArgs.onClick]: defaultButton.onClick,
            [FilterableMenuItemArgs.onRenderIcon]: defaultButton.onRenderIcon,
            [FilterableMenuItemArgs.iconOnly]: !!defaultButton.onRenderIcon,
            [FilterableMenuItemArgs.subMenuProps]: {
                items: menuItems,
            },
        });

        editSplitMenu.iconProps = defaultButton.iconProps;

        return editSplitMenu;
    } else {
        return new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(EditButtonLabel),
            [FilterableMenuItemArgs.icon]: ControlIcons.Edit,
            [FilterableMenuItemArgs.shouldShow]: () => {
                const isGroup =
                    sxsStore.editState.itemId?.mailboxInfo &&
                    sxsStore.editState.itemId.mailboxInfo.type === 'GroupMailbox';
                return (
                    isFeatureEnabled('wvn-sxsEditButtonSplit') &&
                    officeOnlineApp !== null &&
                    sxsStore.readingPaneState &&
                    sxsStore.viewState?.previewPane &&
                    sxsStore.viewState.previewPane.mode === PreviewPaneMode.Wac &&
                    sxsStore.editState.editAllowed &&
                    sxsStore.editState.mode === EditMode.View &&
                    sxsStore.editState.itemId &&
                    !sxsStore.editState.isDraft &&
                    (!isGroup || !isConsumer()) &&
                    !isCloudyAndUsingJsApi(sxsStore)
                );
            },
            [FilterableMenuItemArgs.ariaLabel]: loc(EditButtonLabel),
            [FilterableMenuItemArgs.subMenuProps]: {
                items: menuItems,
            },
        });
    }
}

export function getOfficeAppFromAttachmentId(attachmentId: AttachmentId) {
    if (!attachmentId) {
        return null;
    }

    const attachmentModel = attachmentId && getAttachment(attachmentId);
    return getOfficeAppFromAttachment(attachmentModel);
}

function updateDefaultEditOptionIfNeeded(
    defaultEditOption: EditOptionsCommand,
    newEditOption: EditOptionsCommand
) {
    if (defaultEditOption !== newEditOption) {
        lazySxSDataModule.import().then(sxsDataModule => {
            sxsDataModule.saveDefaultEditCommandOption(newEditOption);
        });
    }
}
